import { BulkTagsModal } from '@/components/admin/response-prompts/BulkTagsModal';
import { ExportModal } from '@/components/admin/response-prompts/ExportModal';
import { ImportResultModal } from '@/components/admin/response-prompts/ImportResultModal';
import { Playground } from '@/components/admin/response-prompts/Playground';
import { PromptDuplicateModal } from '@/components/admin/response-prompts/PromptDuplicateModal';
import { PromptEditorPanel } from '@/components/admin/response-prompts/PromptEditorPanel';
import {
  PromptCreateModal,
  PromptRenameModal,
} from '@/components/admin/response-prompts/PromptNodeModals';
import { ConfirmDeleteModal, PromptTree } from '@/components/admin/response-prompts/PromptTree';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { Tabs, TabsPanel } from '@/components/ui/tabs';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/cn';
import type { ResponsePromptImportResponse, ResponsePromptTreeNode } from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useBlocker } from '@tanstack/react-router';
import { Download, History, Tags, Upload } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

interface CreateState {
  parentId: string | null;
  parentName: string | null;
  type: 'folder' | 'file';
}

interface RenameState {
  id: string;
  name: string;
  description: string | null;
  type: 'folder' | 'file';
  slug: string | null;
}

interface DeleteState {
  id: string;
  name: string;
}

interface DuplicateState {
  id: string;
  name: string;
  slug: string | null;
}

function findNode(tree: ResponsePromptTreeNode[], id: string): ResponsePromptTreeNode | null {
  for (const n of tree) {
    if (n.id === id) return n;
    const c = findNode(n.children, id);
    if (c) return c;
  }
  return null;
}

export function AdminResponseControlPrompts() {
  const qc = useQueryClient();
  const treeQ = useQuery({
    queryKey: ['admin', 'response-prompts', 'tree'],
    queryFn: () => api.responsePromptsTree(),
  });

  const tree = treeQ.data?.tree ?? [];
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
  const [tab, setTab] = useState<'explorer' | 'playground'>('explorer');
  const [editorDirty, setEditorDirty] = useState(false);
  // 모바일 (lg 미만) 에서 트리/에디터 토글. 데스크탑은 무시.
  const [mobileView, setMobileView] = useState<'tree' | 'editor'>('tree');

  const [createState, setCreateState] = useState<CreateState | null>(null);
  const [renameState, setRenameState] = useState<RenameState | null>(null);
  const [deleteState, setDeleteState] = useState<DeleteState | null>(null);
  const [duplicateState, setDuplicateState] = useState<DuplicateState | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [importResult, setImportResult] = useState<ResponsePromptImportResponse | null>(null);
  const [importFatalError, setImportFatalError] = useState<string | null>(null);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /** JSON 데이터를 파일로 다운로드. 전체/선택 둘 다 같은 형식. */
  const downloadExport = (data: unknown, suffix = '') => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const today = new Date().toISOString().slice(0, 10);
    a.download = `response-prompts-${today}${suffix}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportAll = async () => {
    try {
      const data = await api.responsePromptExport();
      downloadExport(data);
    } catch (e) {
      window.alert(`내보내기 실패: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const handleExportSelected = async (ids: string[]) => {
    try {
      const data = await api.responsePromptExportSelected(ids);
      downloadExport(data, `-selected-${ids.length}`);
    } catch (e) {
      window.alert(`내보내기 실패: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const handleImportFile = async (file: File) => {
    setImportResult(null);
    setImportFatalError(null);
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const res = await api.responsePromptImport(data);
      setImportResult(res);
      setImportModalOpen(true);
      invalidate();
    } catch (e) {
      setImportFatalError(e instanceof Error ? e.message : String(e));
      setImportModalOpen(true);
    }
  };

  const guardedSelect = (id: string) => {
    if (id !== selectedFileId) {
      if (
        editorDirty &&
        !window.confirm('저장하지 않은 변경 사항이 있습니다. 그래도 이동하시겠습니까?')
      ) {
        return;
      }
      setSelectedFileId(id);
    }
    setMobileView('editor');
  };

  const backToTree = () => {
    if (
      editorDirty &&
      !window.confirm('저장하지 않은 변경 사항이 있습니다. 그래도 이동하시겠습니까?')
    ) {
      return;
    }
    setMobileView('tree');
  };

  // 다른 라우트로 이동 시 — 사이드바 메뉴 클릭, 뒤로가기 등. enableBeforeUnload 가 활성이면
  // 브라우저 새로고침/탭 닫기에도 native confirm 표시.
  useBlocker({
    shouldBlockFn: () =>
      editorDirty &&
      !window.confirm('저장하지 않은 변경 사항이 있습니다. 그래도 페이지를 떠나시겠습니까?'),
    enableBeforeUnload: () => editorDirty,
  });

  // 트리에서 선택한 파일이 사라진 경우 (조상 폴더 삭제 등) 자동으로 선택 해제.
  // 우측 패널의 "프롬프트를 찾을 수 없습니다" 노출을 회피.
  useEffect(() => {
    if (!selectedFileId || treeQ.isLoading) return;
    if (!findNode(tree, selectedFileId)) setSelectedFileId(null);
  }, [tree, selectedFileId, treeQ.isLoading]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'response-prompts'] });

  const createMut = useMutation({
    mutationFn: (body: {
      parentId: string | null;
      type: 'folder' | 'file';
      name: string;
      description?: string;
      slug?: string;
    }) => api.responsePromptCreate(body),
    onSuccess: (detail) => {
      setCreateState(null);
      if (detail.node.type === 'file') setSelectedFileId(detail.node.id);
      invalidate();
    },
  });

  const renameMut = useMutation({
    mutationFn: (body: { id: string; name: string; description: string; slug?: string }) =>
      api.responsePromptRename(body.id, {
        name: body.name,
        description: body.description,
        slug: body.slug,
      }),
    onSuccess: () => {
      setRenameState(null);
      invalidate();
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.responsePromptDelete(id),
    onSuccess: () => {
      const id = deleteState?.id;
      setDeleteState(null);
      if (id && id === selectedFileId) setSelectedFileId(null);
      invalidate();
    },
  });

  const duplicateMut = useMutation({
    mutationFn: (input: { id: string; name: string; slug: string }) =>
      api.responsePromptDuplicate(input.id, { name: input.name, slug: input.slug }),
    onSuccess: (detail) => {
      setDuplicateState(null);
      setSelectedFileId(detail.node.id);
      invalidate();
    },
  });

  const moveMut = useMutation({
    // sortOrder = 0-based 삽입 인덱스. 서버가 sibling 자동 0..N-1 재번호.
    mutationFn: (input: { id: string; parentId: string | null; targetIndex: number }) =>
      api.responsePromptMove(input.id, {
        parentId: input.parentId,
        sortOrder: input.targetIndex,
      }),
    onSuccess: () => invalidate(),
  });

  const errorBanner = useMemo(() => {
    const e =
      createMut.error ?? renameMut.error ?? deleteMut.error ?? moveMut.error ?? duplicateMut.error;
    if (!e) return null;
    return e instanceof ApiError ? e.message : '요청 처리 중 오류가 발생했습니다.';
  }, [createMut.error, renameMut.error, deleteMut.error, moveMut.error, duplicateMut.error]);

  return (
    <PageShell contentClassName="flex flex-col gap-3 !overflow-hidden">
      <PageHeader
        title="프롬프트 관리"
        description="응답 파이프라인이 끌어다 쓰는 보조 LLM 프롬프트를 폴더 구조로 정리·버저닝합니다."
        actions={
          <div className="flex gap-2">
            <Button type="button" variant="secondary" size="sm" onClick={() => setExportOpen(true)}>
              <Download className="size-4 mr-1" />
              내보내기
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="size-4 mr-1" />
              가져오기
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json,application/json"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleImportFile(f);
                e.target.value = '';
              }}
            />
            <Button type="button" variant="secondary" size="sm" onClick={() => setBulkOpen(true)}>
              <Tags className="size-4 mr-1" />
              일괄 태그
            </Button>
            <Link
              to="/admin/audit"
              search={{ action: 'admin.response_prompt', status: undefined }}
              className="inline-flex"
            >
              <Button type="button" variant="secondary" size="sm">
                <History className="size-4 mr-1" />
                감사 로그
              </Button>
            </Link>
          </div>
        }
      />

      <Tabs
        items={[
          { value: 'explorer', label: '탐색기' },
          { value: 'playground', label: '플레이그라운드' },
        ]}
        value={tab}
        onChange={(v) => setTab(v as 'explorer' | 'playground')}
      />

      {errorBanner ? (
        <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {errorBanner}
        </div>
      ) : null}

      <TabsPanel active={tab === 'explorer'} className="flex-1 min-h-0">
        <div className="flex h-full min-h-120 overflow-hidden rounded-md border border-border bg-surface">
          <div
            className={cn(
              'w-full lg:w-72 lg:shrink-0 lg:border-r lg:border-border',
              mobileView === 'editor' ? 'hidden lg:block' : '',
            )}
          >
            {treeQ.isLoading ? (
              <div className="p-4 text-sm text-foreground-muted">불러오는 중…</div>
            ) : (
              <PromptTree
                tree={tree}
                selectedFileId={selectedFileId}
                onSelect={guardedSelect}
                onMove={(id, parentId, targetIndex) =>
                  moveMut.mutate({ id, parentId, targetIndex })
                }
                onCreate={(parentId, type) => {
                  const parent = parentId ? findNode(tree, parentId) : null;
                  setCreateState({
                    parentId,
                    parentName: parent?.name ?? null,
                    type,
                  });
                }}
                onRename={(id) => {
                  const node = findNode(tree, id);
                  if (!node) return;
                  setRenameState({
                    id,
                    name: node.name,
                    description: node.description,
                    type: node.type,
                    slug: node.slug,
                  });
                }}
                onDelete={(id) => {
                  const node = findNode(tree, id);
                  if (!node) return;
                  setDeleteState({ id, name: node.name });
                }}
                onDuplicate={(id) => {
                  const node = findNode(tree, id);
                  if (!node || node.type !== 'file') return;
                  setDuplicateState({ id, name: node.name, slug: node.slug });
                }}
              />
            )}
          </div>
          <div className={cn('flex-1 min-w-0', mobileView === 'tree' ? 'hidden lg:block' : '')}>
            {selectedFileId ? (
              <PromptEditorPanel
                key={selectedFileId}
                fileId={selectedFileId}
                onDirtyChange={setEditorDirty}
                onBackToTree={backToTree}
              />
            ) : (
              <div className="flex h-full items-center justify-center px-6 text-center text-sm text-foreground-muted">
                {tree.length === 0 ? (
                  <div>
                    <div className="text-base text-foreground mb-1">처음 시작하기</div>
                    <div>
                      왼쪽 상단 <span className="text-accent">"폴더"</span> 또는{' '}
                      <span className="text-accent">"프롬프트"</span> 버튼으로 추가하세요.
                    </div>
                    <div className="mt-2 text-xs">
                      예: 폴더 "질의 분석" → 그 안에 프롬프트 "언어 감지" / "주제 분류"
                    </div>
                  </div>
                ) : (
                  <div>좌측에서 프롬프트(파일)를 선택하세요.</div>
                )}
              </div>
            )}
          </div>
        </div>
      </TabsPanel>

      <TabsPanel active={tab === 'playground'} className="flex-1 min-h-0">
        <div className="h-full min-h-120 overflow-hidden rounded-md border border-border bg-surface">
          <Playground />
        </div>
      </TabsPanel>

      <PromptCreateModal
        open={createState !== null}
        onClose={() => setCreateState(null)}
        type={createState?.type ?? 'folder'}
        parentName={createState?.parentName ?? null}
        isPending={createMut.isPending}
        onSubmit={(input) => {
          if (!createState) return;
          createMut.mutate({
            parentId: createState.parentId,
            type: createState.type,
            name: input.name,
            description: input.description || undefined,
            slug: input.slug,
          });
        }}
      />

      <PromptRenameModal
        open={renameState !== null}
        onClose={() => setRenameState(null)}
        current={renameState}
        isPending={renameMut.isPending}
        onSubmit={(input) => {
          if (!renameState) return;
          renameMut.mutate({
            id: renameState.id,
            name: input.name,
            description: input.description,
            slug: input.slug,
          });
        }}
      />

      <ConfirmDeleteModal
        open={deleteState !== null}
        onClose={() => setDeleteState(null)}
        name={deleteState?.name ?? ''}
        isPending={deleteMut.isPending}
        onConfirm={() => deleteState && deleteMut.mutate(deleteState.id)}
      />

      <PromptDuplicateModal
        open={duplicateState !== null}
        onClose={() => setDuplicateState(null)}
        source={duplicateState}
        isPending={duplicateMut.isPending}
        onSubmit={(input) => {
          if (!duplicateState) return;
          duplicateMut.mutate({ id: duplicateState.id, name: input.name, slug: input.slug });
        }}
      />

      <BulkTagsModal open={bulkOpen} onClose={() => setBulkOpen(false)} tree={tree} />

      <ExportModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        tree={tree}
        onExportAll={handleExportAll}
        onExportSelected={handleExportSelected}
      />

      <ImportResultModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        result={importResult}
        fatalError={importFatalError}
      />
    </PageShell>
  );
}
